/*
    API usage panel (static#9) — reading GET /api-access/usage and turning it
    into the words and colours the panel shows. Pure functions, so every rule
    here is tested without rendering anything.
*/

const PERIODS = ["DAY", "WEEK", "MONTH"];

// The bar and the pill change colour ABOVE 80% and at 100% or more.
const NEAR_LIMIT = 0.8;

const isCount = (value) =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

/*
    Validate the response and keep only what the panel uses.

    Returns null when a number the panel would show is missing or not a
    number. The caller treats null as a failed load, which shows "Usage is
    unavailable right now." instead of a zero: a customer told they have made
    0 calls when the count simply did not arrive is being told something false
    (the same trap as coercing a missing field with Number(): Number(null)
    is 0).
*/
export const parseUsage = (body) => {
  if (!body || typeof body !== "object") return null;
  if (!isCount(body.used) || !isCount(body?.thisMonth?.used)) return null;

  // Only an explicit null means "no call limit". A missing limit is a broken
  // response, and rendering it as an unlimited plan would tell a limited
  // customer something false. A limit needs a period to count against, and
  // an unlimited plan has none.
  const unlimited = body.limit === null;
  if (unlimited ? body.period !== null : !isCount(body.limit)) return null;
  if (!unlimited && !PERIODS.includes(body.period)) return null;

  // The history is part of the contract, not a nice-to-have: a missing array
  // or one bad row fails the whole load rather than quietly dropping months,
  // which would read as "No earlier months yet" or as a gap in the table.
  if (!Array.isArray(body.months)) return null;
  const validMonth = (row) =>
    row &&
    typeof row.month === "string" &&
    /^\d{4}-\d{2}$/.test(row.month) &&
    isCount(row.used);
  if (!body.months.every(validMonth)) return null;

  return {
    period: unlimited ? null : body.period,
    limit: unlimited ? null : body.limit,
    used: body.used,
    resetsAt: typeof body.resetsAt === "string" ? body.resetsAt : null,
    thisMonthUsed: body.thisMonth.used,
    months: body.months.map(({ month, used }) => ({ month, used })),
    historyStartsAt:
      typeof body.historyStartsAt === "string" ? body.historyStartsAt : null,
  };
};

/**
 * "ok" (On track), "near" (above 80%) or "full" (100% or more), plus the bar
 * width as a percentage clamped to 0–100 so it never overflows its track.
 */
export const usageLevel = (used, limit) => {
  if (limit === 0) return { tone: "full", percent: 100 };
  const ratio = used / limit;
  const percent = Math.min(100, Math.max(0, ratio * 100));
  if (ratio >= 1) return { tone: "full", percent };
  if (ratio > NEAR_LIMIT) return { tone: "near", percent };
  return { tone: "ok", percent };
};

const PERIOD_LABELS = {
  DAY: "Today's calls",
  WEEK: "This week's calls",
  MONTH: "This month's calls",
};

export const periodLabel = (period) =>
  PERIOD_LABELS[period] || "Calls this period";

// The calendar date an instant falls on in `timeZone`, as a UTC day number,
// so two instants can be compared by day without time-of-day getting in.
const dayNumber = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(date);
  const get = (type) => Number(parts.find((p) => p.type === type).value);
  return Date.UTC(get("year"), get("month") - 1, get("day")) / 86400000;
};

/*
    When the limit resets, in the viewer's own time zone.

    Returns the phrase that follows "Resets" / "calls are paused until":
      "7:00 PM today", "tomorrow at 7:00 PM", "Saturday at 7:00 PM",
      "Sep 30 at 7:00 PM"
    and `sameDay`, because "Resets at 7:00 PM today" needs an "at" that the
    other three already carry. null when there is no valid reset time.

    `timeZone` is undefined in the app (the browser's zone) and fixed in tests.
*/
export const resetWhen = (resetsAt, now = new Date(), timeZone) => {
  const at = resetsAt ? new Date(resetsAt) : null;
  if (!at || Number.isNaN(at.getTime())) return null;

  // Newer ICU (Chrome 110+, Node 20+) puts a narrow no-break space before
  // "PM". Normalise it so the text is the same in every browser and in tests.
  const time = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
  })
    .format(at)
    .replace(/\u202f/g, " ");
  const days = dayNumber(at, timeZone) - dayNumber(now, timeZone);

  if (days <= 0) return { phrase: `${time} today`, sameDay: true };
  if (days === 1) return { phrase: `tomorrow at ${time}`, sameDay: false };
  if (days < 7) {
    const weekday = new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "long",
    }).format(at);
    return { phrase: `${weekday} at ${time}`, sameDay: false };
  }
  const date = new Intl.DateTimeFormat("en-US", {
    timeZone,
    month: "short",
    day: "numeric",
  }).format(at);
  return { phrase: `${date} at ${time}`, sameDay: false };
};

// Months are UTC calendar months on the backend, so they are labelled in UTC:
// "2026-08" must read "Aug 2026" everywhere, not "Jul 2026" west of Greenwich.
export const monthLabel = (month) => {
  const [year, mon] = month.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "short",
    year: "numeric",
  }).format(new Date(Date.UTC(year, mon - 1, 1)));
};

// "2026-03-03" -> "Mar 3, 2026". A date-only value, so UTC for the same reason.
export const historyDateLabel = (value) => {
  const date = new Date(`${value.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};
